import React from 'react';
import PropTypes from 'prop-types';
import { Snackbar, Slide } from '@mui/material';

/**
 * One transition component per direction, created once at module scope.
 * An inline arrow would be a brand-new component type on every render, which
 * makes MUI remount the transition and kills the slide animation mid-flight.
 */
const TRANSITIONS = {
  up: (props) => <Slide {...props} direction="up" />,
  down: (props) => <Slide {...props} direction="down" />,
  left: (props) => <Slide {...props} direction="left" />,
  right: (props) => <Slide {...props} direction="right" />,
};

const defaultAnchorOrigin = { vertical: 'bottom', horizontal: 'center' };

/**
 * AppSnackbar — Design System transient-notification container.
 *
 * Thin forwardRef wrapper over MUI Snackbar with the slide transition built in.
 * Expects a single child surface (typically AppAlert). Use this instead of
 * importing Snackbar/Slide directly from @mui/material.
 *
 * @param {boolean}  open            Visibility
 * @param {Function} onClose         Close handler (clickaway / autoHideDuration)
 * @param {object}   anchorOrigin    Screen position (default bottom-center)
 * @param {'up'|'down'|'left'|'right'} slideDirection  Enter direction (default 'up')
 */
const AppSnackbar = React.forwardRef(
  (
    {
      open,
      onClose,
      children,
      anchorOrigin = defaultAnchorOrigin,
      slideDirection = 'up',
      sx,
      ...props
    },
    ref,
  ) => (
    <Snackbar
      ref={ref}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      sx={sx}
      {...props}
      // A caller's own slots (incl. a transition) are kept and win over slideDirection.
      slots={{
        transition: TRANSITIONS[slideDirection] || TRANSITIONS.up,
        ...props.slots,
      }}>
      {children}
    </Snackbar>
  ),
);

AppSnackbar.displayName = 'AppSnackbar';

AppSnackbar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.element,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  slideDirection: PropTypes.oneOf(['up', 'down', 'left', 'right']),
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default AppSnackbar;
